import { matchesNameSearch } from "./name-search";

// Which roster an admin is looking at on /rds is a per-user, per-browser
// preference. The key carries the AlwaysAI user id so a shared machine never
// replays one admin's choice for the next sign-in; the stored value is a
// reserved word or a staff user id (never PHI), and it doubles as the
// `?supervisor=` value GET /api/rds reads.
export type RosterScope =
  | { kind: "all" }
  | { kind: "unassigned" }
  | { kind: "supervisor"; supervisorId: string };

const ALL_VALUE = "all";
const UNASSIGNED_VALUE = "unassigned";

export const ALL_RDS_SCOPE: RosterScope = { kind: "all" };
export const UNASSIGNED_RDS_SCOPE: RosterScope = { kind: "unassigned" };

export function rosterScopeStorageKey(userId: string): string {
  return `alwaysai:roster-scope:${userId}`;
}

export function serializeRosterScope(scope: RosterScope): string {
  switch (scope.kind) {
    case "all":
      return ALL_VALUE;
    case "unassigned":
      return UNASSIGNED_VALUE;
    case "supervisor":
      return scope.supervisorId;
  }
}

export function rosterScopeQueryValue(scope: RosterScope): string {
  return serializeRosterScope(scope);
}

export function parseStoredRosterScope(
  stored: string | null,
  knownSupervisorIds: readonly string[],
): RosterScope {
  if (stored === ALL_VALUE) return ALL_RDS_SCOPE;
  if (stored === UNASSIGNED_VALUE) return UNASSIGNED_RDS_SCOPE;
  // A supervisor who has since left the list must not trap the admin on an
  // empty roster, so anything but a current id falls back to the whole roster.
  const isKnownSupervisor = stored !== null && knownSupervisorIds.includes(stored);
  if (isKnownSupervisor) return { kind: "supervisor", supervisorId: stored };
  return ALL_RDS_SCOPE;
}

/**
 * Display names that more than one listed supervisor uses. The registry can
 * hold distinct accounts under one name (the seeded one does), and two
 * identical picker rows give an admin no way to choose the right account, so
 * the picker shows the email only on these. Exact match after trim; look names
 * up trimmed.
 */
export function sharedSupervisorNames(
  supervisors: readonly { id: string; name: string }[],
): Set<string> {
  const seen = new Set<string>();
  const shared = new Set<string>();
  for (const supervisor of supervisors) {
    const name = supervisor.name.trim();
    if (seen.has(name)) shared.add(name);
    seen.add(name);
  }
  return shared;
}

/** The roster title's cmdk filter: name-only, shared with the Appointment Home title. */
export const matchesRosterSearch = matchesNameSearch;

// --- Copy ------------------------------------------------------------------

function rdCount(count: number): string {
  return count === 1 ? "1 RD" : `${count} RDs`;
}

function supervisorCount(count: number): string {
  return count === 1 ? "1 supervisor" : `${count} supervisors`;
}

export function rosterTitle(scope: RosterScope, supervisorName?: string): string {
  switch (scope.kind) {
    case "all":
      return "All RDs";
    case "unassigned":
      return "Unassigned RDs";
    case "supervisor":
      return `${supervisorName ?? "Supervisor"}'s RDs`;
  }
}

export function rosterSubtitle(input: {
  scope: RosterScope;
  rdCount: number;
  supervisorCount: number;
  loading: boolean;
}): string {
  if (input.loading) return "Loading…";
  switch (input.scope.kind) {
    case "all":
      return `${rdCount(input.rdCount)} across ${supervisorCount(input.supervisorCount)}`;
    case "unassigned":
      return input.rdCount === 0 ? "No unassigned RDs" : rdCount(input.rdCount);
    case "supervisor":
      return input.rdCount === 0 ? "No RDs assigned" : rdCount(input.rdCount);
  }
}

/** The phrase in `unassignedNotice().body` the page renders as the registry link. */
export const RD_REGISTRY_LINK_TEXT = "RD registry";

export function unassignedNotice(count: number): { title: string; body: string } {
  if (count === 1) {
    return {
      title: "This RD has no supervisor.",
      body: `Nobody sees it on its own roster, so its sessions go unreviewed. Assign a supervisor in the ${RD_REGISTRY_LINK_TEXT}.`,
    };
  }
  return {
    title: `These ${count} RDs have no supervisor.`,
    body: `Nobody sees them on their own roster, so their sessions go unreviewed. Assign a supervisor in the ${RD_REGISTRY_LINK_TEXT}.`,
  };
}
